import axios, { AxiosResponse } from 'axios'

import { Response } from '@models/apiBase'
import { ID } from '@models/base'
import { CartTypeEnum } from '@models/cart'
import { PatientVisitTypeEnum } from '@models/patientVisit'
import { PrinterDataModel } from '@models/printerData'
import { QueueNodeModel } from '@models/queueNode'
import { QueueNumberPriorityEnum, QueueNumberTypeEnum } from '@models/queueNumber'
import { QueueNumberPreviewModel } from '@models/queueNumberPreview'

import { API_URL } from '@/constants/constant'

const QUEUE_NUMBERS_URL = `${API_URL}/queue-numbers`

type ResponseQueueNumberPreview = Response<{
  queue_number: QueueNumberPreviewModel | null
}>

type ResponseQueueNode = Response<{
  queue_node: QueueNodeModel
}>

type ResponseCreateQueueNumber = Response<{
  printer_data?: PrinterDataModel
  queue_node: QueueNodeModel
  signature: string
}>

type ResponseUploadInsuranceCard = Response<{
  path: string
}>

type PayloadUploadInsuranceCard = {
  image: File
}

type PayloadUpdateQueueNodeStatus = {
  queue_number_id?: ID
  next_queue_id?: ID
  preferred_counter_id?: ID
}

type PayloadMoveQueuePosition = {
  source_queue_id: ID
  queue_number_id: ID
  destination_position?: number | null
  destination_queue_id?: ID
}

type PayloadCreateQueueNumber = {
  cart_type: CartTypeEnum
  insurance_card_filepath: string | null
  insurance_card_number: string | null
  insurance_id: ID | null
  insurance_payor_id: ID | null
  name: string | null
  note: string | null
  patient_id: string | null
  patient_relative_id: ID | null
  patient_visit_type: PatientVisitTypeEnum | null
  preferred_counter_id: ID | null
  priority: QueueNumberPriorityEnum | null
  queue_id: ID | null
  queue_number_type: QueueNumberTypeEnum
}

type PayloadUpdateQueueNumber = {
  insurance_card_filepath: string | null
  insurance_card_number: string | null
  insurance_id: string | null
  insurance_payor_id: string | null
  name: string | null
  note: string | null
  patient_id: ID | null
  patient_relative_id: ID | null
  patient_visit_type: PatientVisitTypeEnum | null
  preferred_counter_id: ID | null
  priority: QueueNumberPriorityEnum | null
}

type PayloadReprintQueueNumber = {
  queue_number: string
}

interface IQueueNumbersAPI {
  get: (id: ID) => Promise<ResponseQueueNode>

  getCurrent: () => Promise<ResponseQueueNumberPreview>

  create: (payload: PayloadCreateQueueNumber) => Promise<ResponseCreateQueueNumber>

  update: (id: ID, payload: PayloadUpdateQueueNumber) => Promise<ResponseQueueNode>

  updateStatus: (
    status: 'complete' | 'call' | 'cancel' | 'repeat' | 'skip' | 'stand-by' | 'process',
    payload?: PayloadUpdateQueueNodeStatus
  ) => Promise<ResponseQueueNode>

  moveQueueNode: (payload: PayloadMoveQueuePosition) => Promise<ResponseQueueNode>

  reprint: (payload: PayloadReprintQueueNumber) => Promise<ResponseCreateQueueNumber>

  uploadInsuranceCard: (
    file: File,
    onUploadProgress?: (ProgressEvent: ProgressEvent) => void,
    controller?: AbortController
  ) => Promise<ResponseUploadInsuranceCard>
}

const QueueNumbersAPI: IQueueNumbersAPI = {
  get: async (id) => {
    return axios.get(`${QUEUE_NUMBERS_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  getCurrent: async () => {
    return axios.get(`${QUEUE_NUMBERS_URL}/current`).then((r: AxiosResponse) => r.data)
  },

  create: async (payload) => {
    return axios.post(QUEUE_NUMBERS_URL, payload).then((r: AxiosResponse) => r.data)
  },

  update: async (id, payload) => {
    return axios.put(`${QUEUE_NUMBERS_URL}/${id}`, payload).then((r: AxiosResponse) => r.data)
  },

  updateStatus: async (status, payload) => {
    return axios.patch(`${QUEUE_NUMBERS_URL}/${status}`, payload).then((r: AxiosResponse) => r.data)
  },

  moveQueueNode: async (payload) => {
    return axios
      .post(`${QUEUE_NUMBERS_URL}/move`, {
        ...payload,
        destination_queue_id: payload.destination_queue_id ?? payload.source_queue_id,
      })
      .then((r: AxiosResponse) => r.data)
  },

  reprint: async (payload) => {
    return axios.post(`${QUEUE_NUMBERS_URL}/reprint`, payload).then((r: AxiosResponse) => r.data)
  },

  uploadInsuranceCard: async (file, onUploadProgress, controller) => {
    const bodyFormData = new FormData()
    bodyFormData.append('file', file)

    return axios
      .post(`${QUEUE_NUMBERS_URL}/upload/insurance-card`, bodyFormData, {
        signal: controller?.signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress,
      })
      .then((r: AxiosResponse) => r.data)
  },
}

export type {
  PayloadCreateQueueNumber,
  PayloadMoveQueuePosition,
  PayloadReprintQueueNumber,
  PayloadUpdateQueueNodeStatus,
  PayloadUpdateQueueNumber,
  PayloadUploadInsuranceCard,
}

export default QueueNumbersAPI
